<script setup lang="ts">
import type { Article } from "~/types/article"
import { TEMPLATES } from "~/utilities/articles"

const props = defineProps<{
  data: Article
  defaultPictureFrameColors?: boolean
}>()

function getPositionX() {
  if (!props.data?.img?.focalPointX)
    return 50
  return (
    (props.data.img.focalPointX / (props.data.img.width ?? 1)) * 100
  )
}

function getPositionY() {
  if (!props.data?.img?.focalPointY)
    return 50
  return (
    (props.data.img.focalPointY / (props.data.img.height ?? 1)) * 100
  )
}

const imageClasses = computed(() => {
  if (!props.data.hasPictureFrame)
    return ""

  switch (props.data.template) {
    case TEMPLATES.TEXT: {
      switch (props.data.imageSize) {
        case "Small":
          return props.data.pictureText?.html
            ? "@md:col-start-5 @md:col-end-[-2]"
            : "@md:col-start-2 @md:col-end-[-2]"
        case "Medium":
          return props.data.pictureText?.html
            ? "@md:col-start-7 @md:col-end-[-2]"
            : "@md:col-start-4 @md:col-end-[-4]"
        default:
          return props.data.pictureText?.html
            ? "@md:col-start-8 @md:col-end-[-2]"
            : "@md:col-start-5 @md:col-end-[-5]"
      }
    }

    case TEMPLATES.IMAGE_WITH_TEXT:
    case TEMPLATES.TEXT_WITH_IMAGE:
    case TEMPLATES.MATH_TASK_WITH_IMAGE: {
      return "@md:col-start-2 @md:col-end-[-2]"
    }

    default:
      return ""
  }
})

const captionClasses = computed(() => {
  if (props.data.template !== TEMPLATES.TEXT)
    return imageClasses.value
  switch (props.data.imageSize) {
    case "Small":
      return "@md:col-start-2 @md:col-end-5"
    case "Medium":
      return "@md:col-start-2 @md:col-end-6"
    default:
      return "@md:col-start-5 @md:col-end-8"
  }
})

const classes = useThemedClasses(() => props.data.pictureColor)
</script>

<template>
  <div
    class="image relative"
    :class="{
      'image--frame u-subgrid grid': data.hasPictureFrame,
      [`${classes.background()} ${classes.bwText()}`]: data.pictureColor,
      'bg-white text-black':
        data.hasPictureFrame && defaultPictureFrameColors,
      'image--left':
        data.template === TEMPLATES.IMAGE_WITH_TEXT ||
        data.template === TEMPLATES.MATH_TASK_WITH_IMAGE,
      'image--right': data.template === TEMPLATES.TEXT_WITH_IMAGE,
      'image--horizontal': data.template === TEMPLATES.TEXT,
    }"
  >
    <figure class="col-start-2 col-end-18 md:contents">
      <img
        class="col-start-2 col-end-18"
        :class="{
          [imageClasses]: true,
          '@md:order-2': data.template === TEMPLATES.TEXT,
        }"
        :src="data.img?.src || '/media/no-image.jpeg'"
        :alt="data.img?.alt"
        :aria-describedby="
          data.img?.detailedDescription
            ? 'detailed-image-description'
            : undefined
        "
        :style="
          data.hasPictureFrame
            ? ''
            : {
              'object-position': `${getPositionX()}% ${getPositionY()}%`,
            }
        "
      />
      <URichText
        v-if="data.img?.detailedDescription"
        id="detailed-image-description"
        class="sr-only"
        :html="data.img?.detailedDescription"
      />
      <figcaption
        v-if="data.hasPictureFrame && data.pictureText?.html"
        class="col-start-2 col-end-18 self-center pt-4 font-national2 md:pt-0"
        :class="{
          '@md:order-1': data.template === TEMPLATES.TEXT,
          'pb-14 md:pb-0': data.imageCaption?.html,
          [captionClasses]: true,
        }"
        v-html="data.pictureText.html"
      />
    </figure>
    <UImageCaption
      v-if="data?.img"
      class="absolute bottom-0 right-0 z-20 w-full text-black"
      :data="data"
    />

    <div class="col-start-8 hidden" />
  </div>
</template>

<style scoped lang="postcss">
.image {
  /* Since subgrid is only in col direction, we need to manually set row-direction gap */
  row-gap: inherit;
}

.image img {
  min-height: 0;
  width: 100%;
  object-fit: cover;
  place-self: stretch;
}

.image:not(.image--frame) img {
  height: 100%;
}

.image > :not(img, figure) {
  min-height: max-content;
  height: max-content;
}

.image--frame {
  @apply py-[var(--grid-col-gap)];

  &.image--left {
    @apply md:place-content-center lg:justify-end;
  }

  &.image--right {
    @apply md:place-content-center lg:justify-start;
  }
}

.image--frame img {
  max-width: 100%;
  max-height: min(80vh, 100%);
  width: auto;
}
</style>
